"use client";

import {
  useCallback,
  useRef,
  type KeyboardEvent,
  type TouchEvent,
} from "react";

export interface PhotoNavigationApi {
  readonly canPrev: boolean;
  readonly canNext: boolean;
  readonly goPrev: () => void;
  readonly goNext: () => void;
  readonly handleKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
  readonly handleTouchStart: (event: TouchEvent<HTMLDivElement>) => void;
  readonly handleTouchEnd: (event: TouchEvent<HTMLDivElement>) => void;
}

const SWIPE_THRESHOLD_PX = 50;

/**
 * Keyboard (Arrow Left/Right) + touch-swipe navigation over an indexed list.
 * Swipe requires ≥50 px of horizontal travel that dominates the vertical
 * delta so vertical scrolls aren't misread as swipes.
 */
export function usePhotoNavigation(
  index: number,
  length: number,
  onIndexChange: (next: number) => void,
): PhotoNavigationApi {
  const canPrev = index > 0;
  const canNext = index < length - 1;

  const goPrev = useCallback(() => {
    if (canPrev) onIndexChange(index - 1);
  }, [canPrev, index, onIndexChange]);

  const goNext = useCallback(() => {
    if (canNext) onIndexChange(index + 1);
  }, [canNext, index, onIndexChange]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        goPrev();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        goNext();
      }
    },
    [goPrev, goNext],
  );

  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  const handleTouchStart = useCallback((event: TouchEvent<HTMLDivElement>) => {
    const touch = event.changedTouches[0];
    if (!touch) return;
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  }, []);

  const handleTouchEnd = useCallback(
    (event: TouchEvent<HTMLDivElement>) => {
      const start = touchStartRef.current;
      touchStartRef.current = null;
      if (!start) return;
      const touch = event.changedTouches[0];
      if (!touch) return;
      const dx = touch.clientX - start.x;
      const dy = touch.clientY - start.y;
      if (Math.abs(dx) < SWIPE_THRESHOLD_PX || Math.abs(dx) < Math.abs(dy)) {
        return;
      }
      if (dx > 0) goPrev();
      else goNext();
    },
    [goPrev, goNext],
  );

  return {
    canPrev,
    canNext,
    goPrev,
    goNext,
    handleKeyDown,
    handleTouchStart,
    handleTouchEnd,
  };
}
